import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';

// "Borrowed" (and adapted) from:
// https://stackblitz.com/edit/video-thumbnail?file=app%2Fvideo-processing-service.ts
@Injectable({ providedIn: 'root' })
export class VideoProcessingService {
  constructor(
    @Inject(DOCUMENT) private document: Document,
    @Inject('Window') private window: Window & typeof globalThis
  ) {}

  isHTML5VideoSupported = () =>
    typeof this.document.createElement('video').canPlayType !== 'undefined' ? true : false;

  public getVideoDuration(videoFile: Blob): Promise<number> {
    if (!this.isHTML5VideoSupported()) {
      return Promise.resolve(1); // Can't really do much here.
    }

    return new Promise((resolve, reject) => {
      const video: HTMLVideoElement = this.document.createElement('video');
      video.preload = 'auto';
      video.src = this.window.URL.createObjectURL(videoFile);
      video.addEventListener('error', () => {
        return Promise.resolve(1); // Can't really do much here
      });
      video.addEventListener('canplay', (event) => {
        this.window.URL.revokeObjectURL(video.src);
        return resolve(video.duration);
      });
      video.load();
    });
  }

  public generateThumbnail(
    videoFile: Blob,
    dimensions: { width: number; height: number }
  ): Promise<string> {
    if (!this.isHTML5VideoSupported()) {
      return Promise.reject();
    }
    const video: HTMLVideoElement = this.document.createElement('video');
    const canvas: HTMLCanvasElement = this.document.createElement('canvas');
    const context: CanvasRenderingContext2D = canvas.getContext('2d');
    return new Promise<string>((resolve, reject) => {
      canvas.addEventListener('error', (err) => {
        this.window.URL.revokeObjectURL(video.src);
        reject(err);
      });
      video.addEventListener('error', (err) => {
        this.window.URL.revokeObjectURL(video.src);
        reject(err);
      });
      video.addEventListener('canplay', (event) => {
        canvas.width = dimensions.width;
        canvas.height = dimensions.height;
        const ratio = video.videoWidth / video.videoHeight;
        let thumbWidth, thumbHeight;
        if (ratio > 1) {
          // landscape
          thumbHeight = dimensions.height;
          thumbWidth = dimensions.width * ratio;
        } else {
          // portrait
          thumbWidth = dimensions.width;
          thumbHeight = dimensions.height * ratio;
        }
        context.translate(canvas.width / 2, canvas.height / 2);
        context.drawImage(video, -thumbWidth / 2, -thumbHeight / 2, thumbWidth, thumbHeight);
        this.window.URL.revokeObjectURL(video.src);
        resolve(canvas.toDataURL());
      });
      if (videoFile.type) {
        video.setAttribute('type', videoFile.type);
      }
      video.preload = 'auto';
      video.src = this.window.URL.createObjectURL(videoFile);
      video.load();
    });
  }
}
